<div class="plan-page">

  <!-- Hero -->
  <section class="hero">
    <div class="container">
      <div class="row align-items-center g-4 g-lg-5">
        <div class="col-lg-6">
          <span class="eyebrow eyebrow-on-dark">Agenda Master for Arthur Murray studios</span>
          <h1 class="hero-title">Run your whole studio from one place</h1>
          <p class="hero-lead">Scheduling, enrollments, payments and franchise reporting in one system built for how Arthur Murray studios actually work.</p>
          <div class="hero-actions">
            <a href="#pricing" class="btn-pill btn-pill-accent" (click)="scrollToSection('pricing'); $event.preventDefault()">See pricing</a>
            <a href="#tour" class="btn-pill btn-pill-ghost" (click)="scrollToSection('tour'); $event.preventDefault()">Take the tour</a>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="screen-frame screen-frame-hero">
            <img src="assets/images/marketing-calendar-2.png" alt="Agenda Master day view showing a full day of lessons across instructors" class="img-fluid">
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Franchise adoption notice -->
  <div class="container">
    <aside class="adoption-note">
      <div class="adoption-icon"><i class="fas fa-rocket" aria-hidden="true"></i></div>
      <div>
        <h2 class="adoption-title">Franchise adoption plan</h2>
        <p class="adoption-text">
          Agenda Master will eventually be required for every Arthur Murray location. Studios that adopt early get
          introductory pricing, a say in what we build next, and priority onboarding. The platform ships regular
          updates as studio owners' needs evolve.
        </p>
      </div>
    </aside>
  </div>

  <!-- Pricing -->
  <section id="pricing" class="section">
    <div class="container">
      <header class="section-header">
        <span class="eyebrow">Pricing</span>
        <h2 class="section-title">One plan, everything included</h2>
        <p class="section-lead">No tiers and no add-ons. Every studio gets the full platform.</p>
      </header>

      <div class="price-card">
        <div class="price-card-body">
          <span class="plan-name">{{ plans[0].plan }} Plan</span>
          <p class="plan-summary">Every tool your front desk, instructors and managers use day to day.</p>

          <h3 class="included-heading">What's included</h3>
          <ul class="included-list">
            <li *ngFor="let feature of plans[0].features">
              <i class="fas fa-check" aria-hidden="true"></i>
              <span>{{ feature }}</span>
            </li>
          </ul>
        </div>

        <div class="price-card-aside">
          <div class="price">
            <span class="price-value">{{ plans[0].price | currency:'USD':'symbol':'1.0-0' }}</span>
            <span class="price-period">/month</span>
          </div>
          <p class="price-note">per studio location, billed monthly</p>

          <ul class="price-facts">
            <li><i class="fas fa-infinity" aria-hidden="true"></i>Unlimited users</li>
            <li><i class="fas fa-file-contract" aria-hidden="true"></i>No long-term contract</li>
            <li><i class="fas fa-cloud" aria-hidden="true"></i>Cloud hosted, regular updates</li>
            <li><i class="fas fa-file-import" aria-hidden="true"></i>Free migration assistance</li>
          </ul>

          <!-- Initial Get Started Button -->
          <div *ngIf="!clientSecret" class="price-cta">
            <button (click)="onSelectPlan(plans[0])"
                    class="btn-pill btn-pill-primary w-100"
                    [disabled]="loading"
                    disabled>
              <span *ngIf="!loading">Request a demo</span>
              <span *ngIf="loading">
                <i class="fas fa-spinner loading-spinner me-2"></i>Loading...
              </span>
            </button>
            <p class="cta-hint">Online sign-up is temporarily unavailable.</p>
          </div>

          <!-- Payment Form -->
          <form *ngIf="clientSecret"
                (submit)="handleSubmit($event)"
                id="payment-form"
                class="payment-form">
            <!-- Payment Element Container -->
            <div id="payment-element">
              <!-- Stripe Payment Element will be inserted here -->
            </div>

            <button type="submit"
                    class="btn-pill btn-pill-primary w-100"
                    [disabled]="loading"
                    disabled>
              <span *ngIf="!loading">Subscribe now</span>
              <span *ngIf="loading">
                <i class="fas fa-spinner loading-spinner me-2"></i>Processing payment...
              </span>
            </button>

            <div class="payment-security">
              <span><i class="fas fa-lock" aria-hidden="true"></i>Secure payment powered by Stripe</span>
              <span><i class="fas fa-building-columns" aria-hidden="true"></i>Pay by ACH or credit card</span>
            </div>
          </form>

          <div *ngIf="errorMessage" class="alert alert-danger mt-3 mb-0" role="alert">
            <i class="fas fa-exclamation-circle me-2"></i>{{ errorMessage }}
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Feature highlights -->
  <section id="features" class="section section-tinted">
    <div class="container">
      <header class="section-header">
        <span class="eyebrow">Features</span>
        <h2 class="section-title">Built for how your studio runs</h2>
      </header>

      <div class="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-4">
        <div class="col" *ngFor="let feature of features">
          <div class="feature-card">
            <div class="feature-icon"><i class="fas fa-{{ feature.icon }}" aria-hidden="true"></i></div>
            <h3 class="feature-title">{{ feature.title }}</h3>
            <p class="feature-text">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Recent releases -->
  <section id="whats-new" class="section section-dark">
    <div class="container">
      <header class="section-header">
        <span class="eyebrow eyebrow-on-dark">Last 12 months</span>
        <h2 class="section-title">Our biggest releases</h2>
        <p class="section-lead">Included in every subscription at no extra cost.</p>
      </header>

      <div class="release-grid">
        <article class="release-card" *ngFor="let release of recentFeatures; let first = first" [class.release-card-featured]="first">
          <div class="release-top">
            <div class="release-icon"><i class="fas fa-{{ release.icon }}" aria-hidden="true"></i></div>
            <span class="release-date">{{ release.released }}</span>
          </div>
          <h3 class="release-title">{{ release.title }}</h3>
          <p class="release-text">{{ release.description }}</p>
        </article>
      </div>
    </div>
  </section>

  <!-- Product tour -->
  <section id="tour" class="section">
    <div class="container">
      <header class="section-header">
        <span class="eyebrow">Product tour</span>
        <h2 class="section-title">See it in action</h2>
      </header>

      <div class="tour-row row align-items-center g-4 g-lg-5">
        <div class="col-lg-5">
          <span class="tour-step">01</span>
          <h3 class="tour-title">Smart scheduling</h3>
          <p class="tour-text">Manage classes, private lessons and studio space on one calendar. Schedule recurring classes, handle cancellations and track attendance, while double-bookings for instructors and rooms are blocked automatically.</p>
        </div>
        <div class="col-lg-7">
          <div class="screen-frame">
            <img src="assets/images/marketing-calendar.png" alt="Day view calendar with lessons for each instructor" class="img-fluid">
          </div>
        </div>
      </div>

      <div class="tour-row row align-items-center g-4 g-lg-5 flex-lg-row-reverse">
        <div class="col-lg-5">
          <span class="tour-step">02</span>
          <h3 class="tour-title">Payments and enrollments</h3>
          <p class="tour-text">Take payments at the front desk with Square Terminal or online through Stripe. Manage enrollments and lesson packages with automatic lesson deductions, and keep payment schedules, refunds and transaction history in one place.</p>
        </div>
        <div class="col-lg-7">
          <div class="screen-frame screen-frame-photo">
            <img src="assets/images/square_terminal.jpg" alt="Square Terminal card reader" class="img-fluid">
          </div>
        </div>
      </div>

      <div class="tour-row row align-items-center g-4 g-lg-5">
        <div class="col-lg-5">
          <span class="tour-step">03</span>
          <h3 class="tour-title">Arthur Murray reports</h3>
          <p class="tour-text">Generate franchise reports from the data you already enter and submit them directly to Arthur Murray, formatted and ready to go.</p>
        </div>
        <div class="col-lg-7">
          <div class="screen-frame screen-frame-device">
            <img src="assets/images/product-demo-calendar.png" alt="Royalty report on a tablet" class="img-fluid">
          </div>
        </div>
      </div>

      <div class="tour-row row align-items-center g-4 g-lg-5 flex-lg-row-reverse">
        <div class="col-lg-5">
          <span class="tour-step">04</span>
          <h3 class="tour-title">Student management</h3>
          <p class="tour-text">Follow each student's progress, attendance and payments from a single dashboard. Bring existing students over from spreadsheets or other dance studio software.</p>
        </div>
        <div class="col-lg-7">
          <div class="screen-frame">
            <img src="assets/images/marketing-student-dashboard-desktop.png" alt="Student dashboard" class="img-fluid">
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- FAQ -->
  <section class="section section-tinted">
    <div class="container">
      <header class="section-header">
        <span class="eyebrow">FAQ</span>
        <h2 class="section-title">Common questions</h2>
      </header>

      <div class="faq-list">
        <details class="faq-item" *ngFor="let faq of faqs; let first = first" [open]="first">
          <summary class="faq-question">
            <span>{{ faq.question }}</span>
            <i class="fas fa-chevron-down" aria-hidden="true"></i>
          </summary>
          <p class="faq-answer">{{ faq.answer }}</p>
        </details>
      </div>
    </div>
  </section>

  <!-- Closing CTA -->
  <section class="closing">
    <div class="container">
      <h2 class="closing-title">Ready to simplify your studio?</h2>
      <ul class="closing-points">
        <li><i class="fas fa-check" aria-hidden="true"></i>No setup fees</li>
        <li><i class="fas fa-check" aria-hidden="true"></i>Free migration assistance</li>
        <li><i class="fas fa-check" aria-hidden="true"></i>Cancel anytime</li>
        <li><i class="fas fa-check" aria-hidden="true"></i>Email &amp; phone support</li>
      </ul>
      <a href="#pricing" class="btn-pill btn-pill-accent" (click)="scrollToSection('pricing'); $event.preventDefault()">See pricing</a>
    </div>
  </section>
</div>
